/** @format */

import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "../supabaseClient";
import { useTranslation } from "react-i18next";
import { specialties } from "../lib/specialties";
import { apiClient } from "../lib/apiClient";
import { useBackendHealth } from "../hooks/useBackendHealth";

interface TransferNotification {
  transferId: number;
  is_urgent: boolean;
  question: string;
  is_read: boolean;
  is_open: boolean;
  is_important: boolean;
  is_ignored: boolean;
  assigned_to: string;
  assigned_by: string;
  collaborations: any[];
  date: Date;
}

interface SessionContextType {
  session: Session | null;
  user: User | null;
  userInfo: any | null;
  loading: boolean;
  loadingNotifications: boolean;
  myUsers: any[];
  notifications: {
    messages: {
      [userId: string]: {
        has: boolean;
        firstAt: string | null;
        unseenTimes: string[];
      };
    };
    transfers: { [userId: string]: TransferNotification[] };
    lastUpdated: number;
  };
  addNotification: (userId: string) => void;
  addTransferNotification: (
    userId: string,
    transfer: TransferNotification
  ) => void;
  clearNotification: (userId: string) => void;
  clearTransferNotification: (userId: string, transferId: number) => void;
  activePhone: any;
  setActivePhone: any;
  activeUserIdRef: any;
  setActiveUserIdRef: any;
  setMyUsers: any;
  isTransferNotification: any;
  setIsTransferNotification: any;
  updateNotificationFlag: (userId: string) => void;
  viewingNotifications: any;
  setViewingNotifications: any;
  language: string;
  setLanguage: any;
  recentUsers: any;
  setRecentUsers: any;
  addRecentUser: any;
  clearUnusedRecentUsers: () => void;
  refreshTokenIfNeeded: () => void;
  returnControlToAi: boolean;
  setReturnControlToAi: any;
  setUserInfo: any;
  company: any;
  updateCompany: any;
  activeNotification: any;
  setActiveNotification: any;
  fetchPendingTransfers: () => Promise<void>;
  isHistoryPage: boolean;
  setIsHistoryPage: any;
  selectedConversationId: any;
  setSelectedConversationId: any;
  selectedConversationLastDate: any;
  setSelectedConversationLastDate: any;
  fetchAllUsersInfo: any;
  allAssistants: any;
  getFullPatientDataByExternalId: any;
  patientIdByPhone: any;
  setPatientIdByPhone: any;
  patientCompleteData: any;
  setPatientCompleteData: any;
  markMessagesSeen: (userId: string | number) => void;
  sidebarAction?: { type: "resolve" | "ignore" | "reassign" } | null;
  setSidebarAction?: (
    a: { type: "resolve" | "ignore" | "reassign" } | null
  ) => void;
  authError: boolean;
  handleAuthError: () => void;
  handleLogoutFromAuthError: () => void;
  roleId?: number | null;
  isBackendOnline: boolean;
  backendLastCheck: Date | null;
  recheckBackend: () => Promise<boolean>;
}

const updateSpecialtyLanguage = (userInfo: any, language: string) => {
  if (userInfo?.specialty_id && specialties[userInfo.specialty_id]) {
    const specialty = specialties[userInfo.specialty_id][language];
    return { ...userInfo, specialty };
  }
  return userInfo;
};

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const [returnControlToAi, setReturnControlToAi] = useState<boolean>(false);
  const [isHistoryPage, setIsHistoryPage] = useState<boolean>(false);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userInfo, setUserInfo] = useState<any | null>(null);
  const [language, setLanguage] = useState<string>("es");
  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [myUsers, setMyUsers] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<{
    messages: {
      [userId: string]: {
        has: boolean;
        firstAt: string | null;
        unseenTimes: string[];
      };
    };
    transfers: { [userId: string]: TransferNotification[] };
    lastUpdated: number;
  }>({
    messages: {} as {
      [userId: string]: {
        has: boolean;
        firstAt: string | null;
        unseenTimes: string[];
      };
    },
    transfers: {},
    lastUpdated: Date.now(),
  });
  const [activePhone, setActivePhone] = useState<string | null>(null);
  const [activeUserIdRef, setActiveUserIdRef] = useState<any>(null);
  const [isTransferNotification, setIsTransferNotification] = useState(false);
  const [viewingNotifications, setViewingNotifications] = useState(true);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [activeNotification, setActiveNotification] = useState<{
    userId: number;
    important: boolean;
    ignored: boolean;
    read: boolean;
    urgent: boolean;
    transferId?: number;
  } | null>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [selectedConversationLastDate, setSelectedConversationLastDate] =
    useState<any>(null);
  const [allAssistants, setAllAssistants] = useState<any[]>([]);
  const [patientIdByPhone, setPatientIdByPhone] = useState<any>(null);
  const [patientCompleteData, setPatientCompleteData] = useState<any>(null);

  const [notifHydrated, setNotifHydrated] = useState(false);

  const [sidebarAction, setSidebarAction] = useState<{
    type: "resolve" | "ignore" | "reassign";
  } | null>(null);

  const [authError, setAuthError] = useState(false);

  const { isOnline: isBackendOnline, lastCheck: backendLastCheck, recheckNow: recheckBackend } = useBackendHealth({
    checkInterval: 15000, 
    timeout: 5000,
    onStatusChange: (isOnline) => {
      if (!isOnline) {
        console.warn('⚠️ Backend connection lost');
      } else {
        console.info('✅ Backend connection restored');
      }
    },
  });

  const handleAuthError = useCallback(() => {
    setAuthError(true);
  }, []);

  const handleLogoutFromAuthError = useCallback(async () => {
    setAuthError(false);
    await supabase.auth.signOut();
    localStorage.clear();
    window.location.href = '/#/login';
  }, []);

  const initializationCompleteRef = useRef(false);
  const fetchCallsInProgressRef = useRef({
    users: false,
    userInfo: false,
    allAssistants: false,
  });
  const lastSessionIdRef = useRef<string | null>(null);

  const markMessagesSeen = (userId: string | number) => {
    setNotifications((prev) => {
      const nextMessages = { ...prev.messages };
      delete (nextMessages as any)[userId];

      return {
        ...prev,
        messages: nextMessages,
        lastUpdated: Date.now(),
      };
    });
  };

  const addRecentUser = (user: any) => {
    setRecentUsers([user]);
  };

  const clearUnusedRecentUsers = () => {
    setRecentUsers([]);
  };

  useEffect(() => {
    if (userInfo) {
      const updatedUserInfo = updateSpecialtyLanguage(userInfo, language);
      setUserInfo(updatedUserInfo);
    }
  }, [language]);

  useEffect(() => {
    let isMounted = true;

    const initializeSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const currentSession = data?.session || null;

        if (!isMounted) return;

        setSession(currentSession);
        setUser(currentSession?.user || null);

        if (currentSession?.user) {
          lastSessionIdRef.current = currentSession.access_token;

          await Promise.all([
            fetchUserInfo(currentSession),
            fetchAllUsersInfo(currentSession),
          ]);

          if (isMounted) {
            initializationCompleteRef.current = true;
            setLoading(false);
          }

        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error("❌ Error en initializeSession:", error);
        if (isMounted) setLoading(false);
      }
    };

    if (!initializationCompleteRef.current) {
      initializeSession();
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!isMounted) return;

      if (event === 'SIGNED_IN' && newSession?.user) {
        const isRealLogin = lastSessionIdRef.current !== newSession.access_token;

        if (isRealLogin) {
          window.bulkHistoryCache = null;
          window.bulkHistoryFetching = false;

          initializationCompleteRef.current = false;
          fetchCallsInProgressRef.current = {
            users: false,
            userInfo: false,
            allAssistants: false,
          };

          setLoading(true);
        }

        lastSessionIdRef.current = newSession.access_token;
        setSession(newSession);
        setUser(newSession.user);

        await Promise.all([
          fetchUserInfo(newSession),
          fetchAllUsersInfo(newSession),
        ]);

        if (isMounted) {
          initializationCompleteRef.current = true;
          setLoading(false);
        }
      } 
      else if (event === 'TOKEN_REFRESHED' && newSession?.user && initializationCompleteRef.current) {
        lastSessionIdRef.current = newSession.access_token;
        setSession(newSession);
        setUser(newSession.user);
        await refreshTokenIfNeeded();
      } 
      else if (event === 'SIGNED_OUT' || !newSession?.user) {
        window.bulkHistoryCache = null;
        window.bulkHistoryFetching = false;
        lastSessionIdRef.current = null;

        setSession(null);
        setUser(null);
        setUserInfo(null);
        setMyUsers([]);
        setAllAssistants([]);
        initializationCompleteRef.current = false;
        fetchCallsInProgressRef.current = {
          users: false,
          userInfo: false,
          allAssistants: false,
        };
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session?.user?.id || !company?.id || notifHydrated) return;
    const key = `notif_state_${company.id}_${session.user.id}`;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const saved = JSON.parse(raw);
        if (
          saved &&
          typeof saved === "object" &&
          Object.keys(saved).length > 0
        ) {
          setNotifications((prev) => ({
            ...prev,
            messages: saved,
            lastUpdated: Date.now(),
          }));
        }
      }
    } catch {}
    setNotifHydrated(true);
  }, [session?.user?.id, company?.id, notifHydrated]);

  useEffect(() => {
    if (!session?.user?.id || !company?.id || !notifHydrated) return;
    const key = `notif_state_${company.id}_${session.user.id}`;
    try {
      localStorage.setItem(key, JSON.stringify(notifications.messages));
    } catch {}
  }, [notifications.messages, company?.id, session?.user?.id, notifHydrated]);

  useEffect(() => {
    if (!company) return;

    fetchPendingTransfers();

    const onInsert = (payload: any) => {
      const {
        id,
        question,
        is_urgent,
        is_read,
        is_open,
        user_id,
        date,
        is_important,
        is_ignored,
        assigned_to,
        collaborations,
        assigned_by,
      } = payload.new;

      const user = myUsers.find((u) => u.id === user_id);
      if (!user) return;

      if (!is_open && user.company_id === company.id) {
        setNotifications((prev) => ({
          ...prev,
          transfers: {
            ...prev.transfers,
            [user_id]: [
              ...(prev.transfers[user_id] || []),
              {
                transferId: id,
                is_urgent,
                question,
                is_read,
                is_open,
                date,
                is_important,
                is_ignored,
                assigned_to,
                collaborations,
                assigned_by,
              },
            ],
          },
          lastUpdated: Date.now(),
        }));
      }
    };

    const onUpdate = async (payload: any) => {
      const newRow = payload.new;
      const oldRow = payload.old;

      const readChanged = oldRow?.is_read !== newRow?.is_read;
      const openChanged = oldRow?.is_open !== newRow?.is_open;
      const assignedChanged = oldRow?.assigned_to !== newRow?.assigned_to;
      const importantChanged = oldRow?.is_important !== newRow?.is_important;
      const ignoredChanged = oldRow?.is_ignored !== newRow?.is_ignored;

      if (
        !readChanged &&
        !openChanged &&
        !assignedChanged &&
        !importantChanged &&
        !ignoredChanged
      )
        return;

      const user = myUsers.find((u) => u.id === newRow.user_id);
      if (!user || user.company_id !== company.id) return;

      try {
        await fetchPendingTransfers();
        await fetchUserInfo(session!);
      } catch (err) {
        console.error("Error refrescando tras cambio de notificación:", err);
      }
    };

    const transferChannel = supabase
      .channel("veterinarian_transfer")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications" },
        onInsert
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "notifications" },
        onUpdate
      )
      .subscribe();

    const messageChannel = supabase
      .channel("mensajes_whatsapp")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "communication_log" },
        (payload) => {
          const { user_id, sender, date_time } = payload.new as any;
          const user = myUsers.find((u) => u.id === user_id);
          if (!user) return;
          if (sender !== "user") return;
          if (user.company_id !== company.id) return;

          setNotifications((prev) => {
            const prevEntry = prev.messages[user_id] || {
              has: false,
              firstAt: null,
              unseenTimes: [],
            };
            const firstAt = prevEntry.firstAt ?? date_time;
            const unseenTimes = [...prevEntry.unseenTimes, date_time];

            return {
              ...prev,
              messages: {
                ...prev.messages,
                [user_id]: { has: true, firstAt, unseenTimes },
              },
              lastUpdated: Date.now(),
            };
          });
        }
      )
      .subscribe();

    return () => {
      transferChannel.unsubscribe();
      messageChannel.unsubscribe();
    };
  }, [company, myUsers, session]);

  useEffect(() => {
    apiClient.configure({
      onAuthError: handleAuthError,
      onTokenRefresh: (newSession) => {
        setSession(newSession);
        localStorage.setItem("session", JSON.stringify(newSession));
      },
    });
  }, []);

  const fetchPendingTransfers = async () => {
    if (!session) return;
    
    try {
      const response = await apiClient.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/getPendingNotifications/${company.id}`,
        {},
        session
      );

      if (!response.ok) {
        console.error("Error fetching pending transfers:", response.statusText);
        return;
      }

      const data = await response.json();

      if (data) {
        const groupedTransfers: { [userId: string]: TransferNotification[] } =
          {};

        data.forEach(
          ({
            id,
            question,
            is_urgent,
            is_read,
            is_open,
            user_id,
            date,
            is_important,
            is_ignored,
            assigned_to,
            collaborations,
            assigned_by,
          }: any) => {
            if (!groupedTransfers[user_id]) groupedTransfers[user_id] = [];
            groupedTransfers[user_id].push({
              transferId: id,
              is_urgent,
              question,
              is_read,
              is_open,
              date,
              is_important,
              is_ignored,
              assigned_to,
              collaborations,
              assigned_by,
            });
          }
        );

        setNotifications((prev) => ({
          ...prev,
          transfers: groupedTransfers,
          lastUpdated: Date.now(),
        }));
      }
    } catch (err: any) {
      console.error("Error fetching pending transfers:", err);
      // Si es un error de autenticación, se manejará automáticamente por apiClient
    }
  };

  const refreshTokenIfNeeded = async () => {
    if (!session) return;

    const tokenExpiry = session.expires_at ? session.expires_at * 1000 : null;
    const now = Date.now();

    if (tokenExpiry && tokenExpiry - now < 60000) {
      try {
        const { data, error } = await supabase.auth.refreshSession();

        if (error) {
          console.error("Error refreshing token:", error.message);
          setSession(null);
          setUser(null);
          setUserInfo(null);
          setMyUsers([]);
        } else {
          setSession(data.session);
          setUser(data.session?.user || null);
        }
      } catch (error) {
        console.error("Error during token refresh:", error);
      }
    }
  };

  const addNotification = (userId: string) => {
    setNotifications((prev) => {
      const prevEntry = prev.messages[userId] || {
        has: false,
        firstAt: null,
        unseenTimes: [],
      };
      return {
        ...prev,
        messages: {
          ...prev.messages,
          [userId]: {
            has: true,
            firstAt: prevEntry.firstAt,
            unseenTimes: prevEntry.unseenTimes,
          },
        },
        lastUpdated: Date.now(),
      };
    });
  };

  const addTransferNotification = (
    userId: string,
    transfer: TransferNotification
  ) => {
    setNotifications((prev) => ({
      ...prev,
      transfers: {
        ...prev.transfers,
        [userId]: [...(prev.transfers[userId] || []), transfer],
      },
      lastUpdated: Date.now(),
    }));
  };

  const denormalizePhone = (phone: string) => {
    phone = phone.replace(/[\s\-()]/g, "");

    if (phone.startsWith("+34")) {
      return phone.replace(/^\+34/, "");
    }

    if (phone.startsWith("0034")) {
      return phone.replace(/^0034/, "");
    }

    if (phone.startsWith("34")) {
      return phone.replace(/^34/, "");
    }

    return phone;
  };

  const updateNotificationFlag = async (userId: string) => {
    try {
      await apiClient.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${userId}`,
        {},
        {},
        session
      );
    } catch (error) {
      console.error("Error updating notification flag:", error);
    }
  };

  const getFullPatientDataByExternalId = async (external_id: string) => {
    try {
      const response = await apiClient.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sina/getFullPatientDataByExternalId/${external_id}`,
        {},
        session
      );

      const data = await response.json();
      setPatientCompleteData(data);
      return data;
    } catch (error) {
      console.error("Error fetching patient data:", error);
      throw error;
    }
  };

  const clearNotification = async (userId: string) => {
    try {
      setNotifications((prev) => {
        const nextMessages = { ...prev.messages };
        const nextTransfers = { ...prev.transfers };
        delete (nextMessages as any)[userId];
        delete (nextTransfers as any)[userId];

        return {
          ...prev,
          messages: nextMessages,
          transfers: nextTransfers,
          lastUpdated: Date.now(),
        };
      });
    } catch (err) {
      console.error("Error updating notification as seen:", err);
    }
  };

  const clearTransferNotification = async (
    userId: string,
    transferId: number
  ) => {
    try {
      setNotifications((prev) => ({
        ...prev,
        transfers: {
          ...prev.transfers,
          [userId]: (prev.transfers[userId] || []).filter(
            (transfer) => transfer.transferId !== transferId
          ),
        },
        lastUpdated: Date.now(),
      }));
    } catch (error) {
      console.error("Error clearing transfer notification:", error);
    }
  };

  const fetchAllUsersInfo = async (session: Session) => {
    if (fetchCallsInProgressRef.current.allAssistants) {
      return;
    }
    fetchCallsInProgressRef.current.allAssistants = true;

    try {
      const response = await apiClient.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/callcenter/`,
        {},
        session
      );

      if (!response.ok) {
        console.error(
          "Error fetching call center assistants:",
          response.statusText
        );
        return;
      }

      const data = await response.json();

      const current = data.find((a: any) => a.id === session.user.id);
      if (current?.language) {
        setLanguage(current.language);
      }

      if (current?.companies) {
        const localCompany = current.companies.find(
          (c: any) => c.id.toString() === localStorage.getItem("company_id")
        );
        updateCompany(localCompany || current.companies[0]);
      }

      updateFaviconAndTitle();
      localStorage.setItem("userInfo", JSON.stringify(current));

      setUserInfo(current);
      setAllAssistants(data);
    } catch (error) {
      console.error("❌ Error fetching call center assistants:", error);
    } finally {
      fetchCallsInProgressRef.current.allAssistants = false;
    }
  };

  const fetchUserInfo = async (session: Session, skipFlag: boolean = false) => {
    if (!skipFlag && fetchCallsInProgressRef.current.userInfo) {
      return null;
    }
    fetchCallsInProgressRef.current.userInfo = true;

    try {
      const response = await apiClient.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/callcenter/${session.user.id}`,
        {},
        session
      );

      if (!response.ok) {
        console.error(
          "Error fetching call center assistant info:",
          response.statusText
        );
        return null;
      }

      const data = await response.json();
      if (data.language) {
        setLanguage(data.language);
      }

      if (data.companies) {
        const localCompany = data.companies.find(
          (c: any) => c.id.toString() === localStorage.getItem("company_id")
        );
        updateCompany(localCompany || data.companies[0]);
      }

      updateFaviconAndTitle();
      setUserInfo(data);
    } catch (error) {
      console.error("❌ Error fetching call center assistant info:", error);
      return null;
    } finally {
      if (!skipFlag) {
        fetchCallsInProgressRef.current.userInfo = false;
      }
    }
  };

  const updateFaviconAndTitle = () => {
    const faviconLink: any = document.querySelector('link[rel="icon"]');

    const isVeterinarian = process.env.REACT_APP_ENV === "veterinarian";

    if (!isVeterinarian) {
      faviconLink.href = "../favicon.svg";
      document.title = t("omnicare");
    } else {
      faviconLink.href = "../faviconVet.svg";
      document.title = t("omnivet");
    }
  };

  const updateCompany = (newCompany: any) => {
    setCompany(newCompany);
    localStorage.setItem("company_id", newCompany.id);
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshTokenIfNeeded();
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [session]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <SessionContext.Provider
      value={{
        session,
        user,
        userInfo,
        setUserInfo,
        language,
        setLanguage,
        company,
        updateCompany,
        loading,
        loadingNotifications,
        myUsers,
        setMyUsers,
        notifications,
        addNotification,
        addTransferNotification,
        clearNotification,
        clearTransferNotification,
        activePhone,
        setActivePhone,
        isTransferNotification,
        setIsTransferNotification,
        updateNotificationFlag,
        viewingNotifications,
        setViewingNotifications,
        recentUsers,
        setRecentUsers,
        addRecentUser,
        clearUnusedRecentUsers,
        refreshTokenIfNeeded,
        returnControlToAi,
        setReturnControlToAi,
        activeNotification,
        setActiveNotification,
        fetchPendingTransfers,
        isHistoryPage,
        setIsHistoryPage,
        selectedConversationId,
        setSelectedConversationId,
        selectedConversationLastDate,
        setSelectedConversationLastDate,
        fetchAllUsersInfo,
        allAssistants,
        getFullPatientDataByExternalId,
        patientIdByPhone,
        setPatientIdByPhone,
        patientCompleteData,
        setPatientCompleteData,
        markMessagesSeen,
        sidebarAction,
        setSidebarAction,
        activeUserIdRef,
        setActiveUserIdRef,
        authError,
        handleAuthError,
        handleLogoutFromAuthError,
        roleId: userInfo?.role_id || null,
        isBackendOnline,
        backendLastCheck,
        recheckBackend,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};
